/* eslint-disable react-hooks/exhaustive-deps */
import classNames from 'classnames';
import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { REQUIREMENTSTRUCTURE_API_BASE, REQUIREMENTS_API_BASE, WBS_ELEMENT_BASE_URL } from '../../../../api/api';
import { deleteAPIRequest, getAPIRequest, useGenericAPI } from '../../../../api/useGenericApi';
import { useWBSElement } from '../../../../api/useWBSElement';
import { ProButton } from '../../../../atoms/Button/ProButton';
import { ProTable } from '../../../../atoms/Table/Table';
import { ApplicationElementType, getBaseBCItemForType } from '../../../../components/DetailWindow/breadCrump.helpers';
import { ProPage } from '../../../../components/Page/Page';
import { ProjectContext } from '../../../../contexts/ProjectContext';
import { UserContext } from '../../../../contexts/UserContext';
import { useWBSDetailWindow } from '../../../../modules/Windows/WBSElement/WBSElementHook/WBSDetailFrame';
import { ProIntent } from '../../../../utils/enums';
import { showErrorToast } from '../../../../utils/helpers';
import { Components } from '../../../../utils/types';
import { RequirementContextProvider } from '../../Requirements/context/RequirementContext';
import {
    RequirementDetailWindowType,
    useRequirementDetailsFrame,
} from '../../Requirements/modules/RequirementDetailsFrame';
import { getPlanningSprintTableData, getSprintPlanningTableData } from './SprintPlanning.helpers';

export const SprintPlanningPage = ({
    setSelectedSprint,
    selectedSprint,
}: {
    selectedSprint: Components.Schemas.WBSElementDeltaDTO;
    setSelectedSprint: Dispatch<SetStateAction<Components.Schemas.WBSElementDeltaDTO | undefined>>;
}) => {
    const { t } = useTranslation();
    const { selectedProject, loading: projectLoading, error: projectError, selectedTeam } = useContext(ProjectContext);
    const [selectedRequirement, setSelectedRequirement] = useState<Components.Schemas.RequirementDTO>();
    const [selectedTableRow, setSelectedTableRow] = useState<number>();
    const [sprintOpen, setSprintOpen] = useState(false);
    const { portfolio } = useContext(UserContext);
    const { sync: projectSync } = useContext(ProjectContext);

    const toggleSprintOpen = () => {
        setSprintOpen((cur) => !cur);
    };

    const deselectTableRow = () => {
        setSelectedTableRow(undefined);
        setSelectedRequirement(undefined);
        open('hidden');
        openWBS('hidden', false);
    };

    const selectSprint = () => {
        setSelectedTableRow(undefined);
        setSelectedRequirement(undefined);
        setSprintOpen(true);
        open('hidden');
        openWBS('view');
    };

    const syncWBSScructure = () => {};

    const {
        open: openWBS,
        renderWBSDetailWindow,
        isDisplayed: wbsIsDisplayed,
        mode: detailWindowMode,
    } = useWBSDetailWindow('Sprint', deselectTableRow, selectedSprint, syncWBSScructure);

    const selectedRequStruct = useMemo(
        () => selectedProject?.requirementStructures?.find((cur) => cur.version === 'Forecast'),
        [selectedProject]
    );

    const selectElement = (id?: string) => {
        const req = requirementStructure?.requirements?.find((cur) => cur.id === id);

        if (req) {
            open('view');
            openWBS('hidden', false);
            setSelectedRequirement(req);
        }
    };

    const { updateWBSElement } = useWBSElement(selectedSprint);
    const startSprint = () => {
        const inProgress = portfolio?.wbsCompletionStates?.find((cur) => cur.completionStateType === 'InProgress');
        updateWBSElement({ ...selectedSprint, wbsCompletionState: inProgress }, () => setSelectedSprint(undefined));
    };

    const {
        data: requirementStructure,
        error,
        loading,
        sync,
    } = useGenericAPI<Components.Schemas.RequirementStructureDTO>(
        `${REQUIREMENTSTRUCTURE_API_BASE}/${selectedRequStruct?.id}/Team`,
        selectedTeam?.id
    );

    const { isDisplayed, mode, open, renderDetailWindow } = useRequirementDetailsFrame({
        closeAction: deselectTableRow,
        id: selectedRequirement?.id,
        selectAction: selectElement,
        externalSync: sync,
        type: RequirementDetailWindowType.Planning,
    });

    const syncPage = () => {
        getAPIRequest<Components.Schemas.WBSElementDeltaDTO>(WBS_ELEMENT_BASE_URL, selectedSprint.id!)
            .then((res) => {
                setSelectedSprint(res);
                sync();
            })
            .catch(() => {
                showErrorToast('Cant update sprint.');
            });
    };

    const requirements = useMemo(
        () =>
            requirementStructure?.requirements
                ?.filter((cur) => cur.requirementType === 'Bug' || cur.requirementType === 'UserStory')
                .filter((cur) => cur.requirementState?.name === 'ready')
                .filter((cur) => !cur.wbsElement) ?? [],
        [requirementStructure]
    );

    const deleteAction = useMemo(
        () => (i: number) => {
            const req = requirements[i];
            if (req.id) {
                deleteAPIRequest(req.id ?? '0', REQUIREMENTS_API_BASE)
                    .then(() => {
                        sync();
                    })
                    .catch(() => {
                        showErrorToast('Cant delete element.');
                    });
            }
        },
        [requirements, sync]
    );

    const tableData = useMemo(
        () =>
            getSprintPlanningTableData({
                deleteAction,
                isDisplayed: isDisplayed || wbsIsDisplayed,
                mode,
                requirements,
                selectElement,
                t,
                portfolio,
                selectedSprint,
                sync: syncPage,
            }),
        [deleteAction, isDisplayed, mode, requirements, selectElement, t]
    );

    const sprintTableData = useMemo(
        () =>
            getPlanningSprintTableData({
                selectElement,
                t,
                selectedSprint,
                selectSprint,
                sprintOpen,
                toggleSprintOpen,
                wbsIsDisplayed,
                sync,
                portfolio,
            }),
        [deleteAction, isDisplayed, mode, requirements, selectElement, t]
    );

    //set the corresponding row to active when selected item changed
    useEffect(() => {
        // +1 -1 is a hack to make findIndex => -1 into 0 wich is nullable
        const row = requirements.findIndex((cur) => cur.id === selectedRequirement?.id) + 1;
        setSelectedTableRow(row - 1);
    }, [requirements, selectedRequirement]);

    return (
        <ProPage
            breadCrump={getBaseBCItemForType(
                [
                    ApplicationElementType.TeamManagement,
                    { type: ApplicationElementType.TeamManagement, name: selectedTeam?.name },
                ],
                t
            )}
            name={'Sprint planning / Scheduling'}
            loading={loading}
            filter={
                <>
                    <ProButton
                        text={'Finish planning'}
                        mainIntent={ProIntent.Gray}
                        onClick={() => setSelectedSprint(undefined)}
                    />
                    <ProButton text={'Start sprint'} mainIntent={ProIntent.Success} onClick={startSprint} />
                </>
            }
        >
            <RequirementContextProvider>
                {selectedProject && tableData && (
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <ProTable
                            data={sprintTableData}
                            rowsCount={sprintOpen ? (selectedSprint.requirements?.length || 0) + 1 : 1}
                            selectedTableRow={selectedTableRow}
                            getRowClass={(i) => (i === 0 ? `ProTable--row__level-0` : `ProTable--row__level-1`)}
                            isMultiTable
                        />
                        <ProTable
                            data={tableData}
                            rowsCount={requirements?.length}
                            selectedTableRow={selectedTableRow}
                            getRowClass={(i) => `ProTable--row__level-0 `}
                            typeForErrorMsg="ready stories"
                        />
                    </div>
                )}
                <div
                    className={classNames(
                        'ProApp--detailWindowSection',
                        (isDisplayed || wbsIsDisplayed) && 'ProApp--detailWindowSection__expanded'
                    )}
                >
                    {renderDetailWindow()}
                    {renderWBSDetailWindow()}
                </div>
            </RequirementContextProvider>
        </ProPage>
    );
};
